import {
  Api32,
  Calculator32,
  Calibrate32,
  DataCheck32,
  DataShare32,
  Delivery32,
  Finance32,
  Home32,
  Identification32,
  ListDropdown32,
  QueryQueue32,
} from "@carbon/icons-react";
import { featureFlag } from "config";

/* Groups */

enum Group {
  Borrowers = "Borrowers",
  CapitalMarkets = "Capital markets",
  DisclosurePreparation = "Disclosure preparation",
  Engineering = "Engineering",
  LoanProduct = "Loan product",
  Other = "Other",
  PostClosing = "Post-closing",
  QualityManagement = "Quality management",
  SubjectProperty = "Subject property",
  Underwriting = "Underwriting",
  VendorRequests = "Vendor requests",
}

/* Screens */

// NOTE: jjw: commented-out screens/links are currently hidden; not removing them yet as they exist
// in TRUS, so it's possible we'll want to add them back again based on additional feedback from ops
// (goal is to have entries here correspond 1-to-1 with extant screens, even if commented-out)

// NOTE: jjw: for searchability, consts here should correspond exactly to the current hurdle name
// minus "hurdle"; it's alright if the display text is changed to something more specific, however
export enum Screen {
  AmortizationSchedule = "Amortization schedule",
  AppraisalReport = "Appraisal order",
  AssetSummary = "Asset summary",
  BorrowerRateLock = "Borrower rate lock",
  CashCloseSummary = "Cash close summary",
  CQMBasicInfoReview = "QC basic review",
  CQMDocMagicReview = "QC DocMagic warnings review",
  CQMMiscInfoReview = "QC property docs review",
  ClosingExplanations = "Closing explanations",
  ClosingSummary = "Closing summary",
  DisclosureGeneration = "Disclosure generation",
  FinancialReporting = "Financial reporting",
  Conditions = "Conditions",
  ConflictOfInterest = "Conflict of interest review",
  ContactInfo = "Contact info",
  CreditReportPull = "Credit report",
  CreditSummary = "Credit summary",
  Declarations = "Declarations",
  DesktopUnderwriter = "AUS Desktop Underwriter",
  DisclosureAPRMismatch = "Disclosure APR mismatch",
  DisclosureEntities = "Disclosure entities",
  DisclosureRegeneration = "Disclosure regeneration",
  DisclosureRequestLintResults = "Disclosure request lint results",
  DocmagicWarnings = "DocMagic warnings",
  Documents = "Documents",
  EmailConfirmed = "Email confirmed",
  Employment = "Employment history [Old]",
  Escrows = "Escrows",
  Explanations = "Letter of explanation summary",
  Fees = "Fees",
  Flags = "Flags",
  FloodCertification = "Flood certification",
  Fraud = "LoanSafe Fraud Manager",
  Funding = "Funding",
  HazardInsurance = "Homeowner's insurance review",
  History = "Address history",
  InvestorRateLock = "Investor rate lock",
  Income = "Income",
  // IncomeDocumentsReview = "Income documents review",
  // IncomeSummary = "Income summary",
  Tasks = "Tasks",
  LoanFileWorksheet = "Loan File worksheet",
  LoanProductScenario = "Loan product scenario",
  LoanProduct = "Loan product",
  Messages = "Messages",
  MortgageInsuranceSummary = "Mortgage insurance",
  OwnerOccupancy = "Occupancy review",
  PersonalInfo = "Personal info",
  PointsAndCredits = "Points & credits",
  PostClosingDocs = "Post-closing docs",
  Prepaids = "Prepaids",
  PropertyCondition = "Property condition review",
  PropertyType = "Property type review",
  // SalariedIncomeAnalysis = "Salaried income analysis",
  SandboxScenario = "Loan sandbox",
  SecondLien = "Simultaneous second lien",
  SpecialSituations = "Special situations",
  SubjectPropertyReport = "Subject property report",
  // TaxReturnIncomeAnalysis = "Tax return income analysis",
  TaxTranscripts = "Tax return transcripts",
  TaxTranscriptsMatchIngestedReturns = "Tax transcripts match ingested returns",
  Title = "Title order",
  TitleCommitment = "Title commitment review",
  TransactionTerms = "Transaction terms",
  UnderwritingHub = "Underwriting hub",
  UnderwritingSummary = "Underwriting summary",
  VALoans = "VA loan review",
  VerbalVerificationOfEmployment = "Verbal verification of employment",
  Vesting = "Title vesting summary",
  WrittenVerificationOfEmployment = "Written verification of employment",
}

const ScreenLinks: { [key in Screen]: string } = {
  [Screen.AmortizationSchedule]: "AmortizationSchedule",
  [Screen.AppraisalReport]: "AppraisalReportHurdle",
  [Screen.AssetSummary]: "AssetSummaryHurdle",
  [Screen.BorrowerRateLock]: "BorrowerRateLockHurdle",
  [Screen.CashCloseSummary]: "CashCloseSummaryHurdle",
  [Screen.CQMBasicInfoReview]: "CQMBasicInfoReviewHurdle",
  [Screen.CQMDocMagicReview]: "CQMDocMagicReviewHurdle",
  [Screen.CQMMiscInfoReview]: "CQMMiscInfoReviewHurdle",
  [Screen.ClosingExplanations]: "ClosingExplanationsHurdle",
  [Screen.ClosingSummary]: "ClosingSummaryHurdle",
  [Screen.DisclosureGeneration]: "DisclosureGeneration",
  [Screen.FinancialReporting]: "FinancialReporting",
  [Screen.Conditions]: "ConditionsHurdle",
  [Screen.ConflictOfInterest]: "ConflictOfInterestHurdle",
  [Screen.ContactInfo]: "ContactInfoHurdle",
  [Screen.CreditReportPull]: "CreditReportPullHurdle",
  [Screen.CreditSummary]: "CreditSummaryHurdle",
  [Screen.Declarations]: "DeclarationsHurdle",
  [Screen.DesktopUnderwriter]: "DesktopUnderwriterHurdle",
  [Screen.DisclosureAPRMismatch]: "DisclosureAPRMismatchHurdle",
  [Screen.DisclosureEntities]: "DisclosureEntitiesHurdle",
  [Screen.DisclosureRegeneration]: "DisclosureRegenerationHurdle",
  [Screen.DisclosureRequestLintResults]: "DisclosureRequestLintResultsHurdle",
  [Screen.DocmagicWarnings]: "DocmagicWarningsHurdle",
  [Screen.Documents]: "DocumentsHurdle",
  [Screen.EmailConfirmed]: "EmailConfirmedHurdle",
  [Screen.Employment]: "EmploymentHurdle",
  [Screen.Escrows]: "EscrowsHurdle",
  [Screen.Explanations]: "ExplanationsHurdle",
  [Screen.Fees]: "FeesHurdle",
  [Screen.Flags]: "FlagsHurdle",
  [Screen.FloodCertification]: "FloodCertificationHurdle",
  [Screen.Fraud]: "FraudHurdle",
  [Screen.Funding]: "FundingHurdle",
  [Screen.HazardInsurance]: "HazardInsuranceHurdle",
  [Screen.History]: "HistoryHurdle",
  [Screen.Income]: "Income",
  [Screen.InvestorRateLock]: "InvestorRateLockHurdle",
  // [Screen.IncomeDocumentsReview]: "IncomeDocumentsReviewHurdle",
  // [Screen.IncomeSummary]: "IncomeSummaryHurdle",
  [Screen.Tasks]: "TasksHurdle",
  [Screen.LoanFileWorksheet]: "LoanFileWorksheetHurdle",
  [Screen.LoanProduct]: "LoanProductHurdle",
  [Screen.LoanProductScenario]: "LoanProductScenario",
  [Screen.Messages]: "MessagesHurdle",
  [Screen.MortgageInsuranceSummary]: "MortgageInsuranceSummaryHurdle",
  [Screen.OwnerOccupancy]: "OwnerOccupancyHurdle",
  [Screen.PersonalInfo]: "PersonalInfoHurdle",
  [Screen.PointsAndCredits]: "PointsAndCredits",
  [Screen.PostClosingDocs]: "PostClosingDocsHurdle",
  [Screen.Prepaids]: "PrepaidsHurdle",
  [Screen.PropertyCondition]: "PropertyConditionHurdle",
  [Screen.PropertyType]: "PropertyTypeHurdle",
  // [Screen.SalariedIncomeAnalysis]: "SalariedIncomeAnalysisHurdle",
  [Screen.SandboxScenario]: "SandboxScenario",
  [Screen.SecondLien]: "SecondLienHurdle",
  [Screen.SpecialSituations]: "SpecialSituationsHurdle",
  [Screen.SubjectPropertyReport]: "SubjectPropertyReportHurdle",
  // [Screen.TaxReturnIncomeAnalysis]: "TaxReturnIncomeAnalysisHurdle",
  [Screen.TaxTranscripts]: "TaxTranscriptsHurdle",
  [Screen.TaxTranscriptsMatchIngestedReturns]: "TaxTranscriptsMatchIngestedReturnsHurdle",
  [Screen.Title]: "TitleHurdle",
  [Screen.TitleCommitment]: "TitleCommitmentHurdle",
  [Screen.TransactionTerms]: "TransactionTermsHurdle",
  [Screen.UnderwritingHub]: "UnderwritingHubHurdle",
  [Screen.UnderwritingSummary]: "UnderwritingSummaryHurdle",
  [Screen.VALoans]: "VALoans",
  [Screen.VerbalVerificationOfEmployment]: "VerbalVerificationOfEmploymentHurdle",
  [Screen.Vesting]: "VestingHurdle",
  [Screen.WrittenVerificationOfEmployment]: "WrittenVerificationOfEmploymentHurdle",
};

interface ScreenToSidebarLink {
  name: Screen;
  to: string;
}

const toLink = (s: Screen): ScreenToSidebarLink => ({
  name: s,
  to: ScreenLinks[s],
});

const incomeScreenLink = {
  name: "Income [Old]",
  to: "income",
};

export const OrderedGroups = [
  {
    name: Group.Borrowers,
    icon: Identification32,
    links: [
      toLink(Screen.PersonalInfo),
      toLink(Screen.History),
      toLink(Screen.Employment),
      toLink(Screen.Declarations),
      toLink(Screen.CreditSummary),
      toLink(Screen.Income),
      incomeScreenLink,
      // toLink(Screen.IncomeSummary),
      // toLink(Screen.IncomeDocumentsReview),
      // toLink(Screen.SalariedIncomeAnalysis),
      // toLink(Screen.TaxReturnIncomeAnalysis),
      toLink(Screen.AssetSummary),
      toLink(Screen.Explanations),
      toLink(Screen.VALoans),
    ].filter(Boolean),
  },
  {
    name: Group.SubjectProperty,
    icon: Home32,
    links: [
      toLink(Screen.PropertyType),
      toLink(Screen.PropertyCondition),
      toLink(Screen.HazardInsurance),
      toLink(Screen.TitleCommitment),
      toLink(Screen.ConflictOfInterest),
      toLink(Screen.OwnerOccupancy),
    ],
  },
  {
    name: Group.LoanProduct,
    icon: QueryQueue32,
    links: [
      toLink(Screen.LoanProduct),
      toLink(Screen.SandboxScenario),
      toLink(Screen.PointsAndCredits),
      toLink(Screen.TransactionTerms),
      toLink(Screen.MortgageInsuranceSummary),
      toLink(Screen.SecondLien),
      toLink(Screen.CashCloseSummary),
    ],
  },
  {
    name: Group.Underwriting,
    icon: Calculator32,
    links: [toLink(Screen.UnderwritingSummary), toLink(Screen.DesktopUnderwriter)],
  },
  {
    name: Group.VendorRequests,
    icon: DataShare32,
    links: [
      toLink(Screen.WrittenVerificationOfEmployment),
      toLink(Screen.VerbalVerificationOfEmployment),
      toLink(Screen.TaxTranscripts),
      toLink(Screen.AppraisalReport),
      toLink(Screen.Title),
      toLink(Screen.FloodCertification),
      toLink(Screen.SubjectPropertyReport),
      toLink(Screen.CreditReportPull),
      toLink(Screen.Fraud),
    ],
  },
  {
    name: Group.DisclosurePreparation,
    icon: ListDropdown32,
    links: [
      toLink(Screen.DisclosureGeneration),
      // AAN do not have a dedicated hurdle screen
      // Let's navigate to their location on the single loan app page view
      {
        name: "Adverse Action Notice",
        to: "#AdverseActionNotice",
      },
      toLink(Screen.ClosingSummary),
      toLink(Screen.Fees),
      toLink(Screen.Prepaids),
      toLink(Screen.Escrows),
      toLink(Screen.Vesting),
      toLink(Screen.Funding),
    ],
  },
  {
    name: Group.PostClosing,
    icon: Delivery32,
    links: [toLink(Screen.AmortizationSchedule), toLink(Screen.PostClosingDocs)],
  },
  {
    name: Group.CapitalMarkets,
    icon: Finance32,
    links: [
      toLink(Screen.BorrowerRateLock),
      toLink(Screen.InvestorRateLock),
      toLink(Screen.FinancialReporting),
    ],
  },
  {
    name: Group.QualityManagement,
    icon: DataCheck32,
    links: [
      toLink(Screen.CQMBasicInfoReview),
      toLink(Screen.CQMMiscInfoReview),
      toLink(Screen.CQMDocMagicReview),
    ],
  },
  {
    name: Group.Engineering,
    icon: Calibrate32,
    links: [
      toLink(Screen.DisclosureEntities),
      toLink(Screen.DisclosureRegeneration),
      toLink(Screen.DisclosureRequestLintResults),
      toLink(Screen.LoanFileWorksheet),
      toLink(Screen.LoanProductScenario),
    ],
  },
  {
    name: Group.Other,
    icon: Api32,
    links: [
      toLink(Screen.ClosingExplanations),
      // toLink(Screen.Conditions),
      // toLink(Screen.ContactInfo),
      toLink(Screen.DisclosureAPRMismatch),
      toLink(Screen.DocmagicWarnings),
      // toLink(Screen.Documents),
      toLink(Screen.EmailConfirmed),
      // toLink(Screen.Flags),
      // toLink(Screen.Tasks),
      toLink(Screen.SpecialSituations),
      toLink(Screen.TaxTranscriptsMatchIngestedReturns),
      toLink(Screen.UnderwritingHub),
    ],
  },
];

export const screenUrl = (appId: number, s: Screen): string =>
  `/underwriter/app/${appId}/${ScreenLinks[s]}`;

export const screenIsActive = (location, s: Screen): boolean =>
  location.pathname.includes(ScreenLinks[s]);

/* Tools */

export enum ToolPanelEnum {
  Conditions = "Conditions",
  Flags = "Flags",
  Notes = "Notes & Events",
}

export const panelToFullToolUrl = (appId: number, p: ToolPanelEnum): string => {
  if (p === ToolPanelEnum.Conditions) {
    return screenUrl(appId, Screen.Conditions);
  }
  if (p === ToolPanelEnum.Flags) {
    return screenUrl(appId, Screen.Flags);
  }
  if (p === ToolPanelEnum.Notes) {
    return `/underwriter/app/${appId}/notes`;
  }
  throw new Error("not reached");
};
